import { ClientInferResponseBody } from "@ts-rest/core";
import { z } from "zod";

import {
  americanIndianOrAlaskaNativeStatusEnumSchema,
  ApplicationLastStepEnumType,
  ApplicationTypeEnumType,
  childCaregiverStatusEnumSchema,
  citizenshipStatusEnumSchema,
  healthInsuranceOrCoverageStatusEnumSchema,
  medicarePlanTypeEnumSchema,
  needsHelpDailyActivitiesStatusEnumSchema,
  patientsApiContract,
  studentFullTime18to20StatusEnumSchema,
  veteranOrMilitaryMemberStatusEnumSchema,
} from "@libs/api/patients";
import { HTTPStatusCode } from "@libs/api/types";

export const createApplicationBodySchema =
  patientsApiContract.applications.createApplication.body;

export type CreateApplicationType = z.infer<typeof createApplicationBodySchema>;

// Internal form type uses string[] for checkbox compatibility
export type CreateApplicationFormType = Omit<
  CreateApplicationType,
  "householdMembers"
> & {
  householdMembers: string[];
};

const listApplicationsQuerySchema =
  patientsApiContract.applications.listApplications.query;

export const listApplicationsFilterSchema = listApplicationsQuerySchema;

export type ListApplicationsFilterType = z.infer<
  typeof listApplicationsFilterSchema
>;

export type RecentDuplicateApplicationSelectionType = z.infer<
  typeof patientsApiContract.applications.listRecentDuplicateApplications.query
>["selectedApplications"][number];

export type RecentDuplicateApplicationType = ClientInferResponseBody<
  typeof patientsApiContract.applications.listRecentDuplicateApplications,
  typeof HTTPStatusCode.OK
>[number];

export const updateApplicationSchema =
  patientsApiContract.applications.updateApplication.body;
export type UpdateApplicationType = z.infer<typeof updateApplicationSchema>;

export const applicationSchema =
  patientsApiContract.applications.getApplication.responses[HTTPStatusCode.OK];

export type ApplicationType = z.infer<typeof applicationSchema>;

// The list endpoint extends each application with a `brands` array (used to
// compose the brand label on submitted-application cards).
export type ListApplicationsResponseType = ClientInferResponseBody<
  typeof patientsApiContract.applications.listApplications,
  typeof HTTPStatusCode.OK
>;

export type ListedApplicationType = ListApplicationsResponseType[number];

export type CreateApplicationResponseType = ClientInferResponseBody<
  typeof patientsApiContract.applications.createApplication,
  typeof HTTPStatusCode.CREATED
>;

/**
 * A member submitted for Medicaid or Medicare is very likely to end up covered
 * there, which makes a simultaneous Marketplace submission a cold lead for the
 * broker who picks it up.
 */
export const CONFLICTING_PROGRAMS: Record<
  ApplicationTypeEnumType,
  ApplicationTypeEnumType[]
> = {
  MEDICAID: ["MARKETPLACE"],
  MEDICARE: ["MARKETPLACE"],
  MARKETPLACE: ["MEDICAID", "MEDICARE"],
};

export type ProgramConflictType = {
  newProgram: ApplicationTypeEnumType;
  conflictingPrograms: ApplicationTypeEnumType[];
  memberIdsToSelect: string[];
  conflictingMemberIds: string[];
  shouldRecommendMedicaid: boolean;
};

type FindProgramConflictParamsType = {
  newProgram: ApplicationTypeEnumType;
  memberIdsToSelect: string[];
  selectedMemberIdsByProgram: Record<ApplicationTypeEnumType, string[]>;
  medicaidEligibleMemberIds: string[];
};

/**
 * Only newly added members are considered — removing a member from a program
 * can never create a conflict.
 */
export const findProgramConflict = ({
  newProgram,
  memberIdsToSelect,
  selectedMemberIdsByProgram,
  medicaidEligibleMemberIds,
}: FindProgramConflictParamsType): ProgramConflictType | null => {
  const alreadySelectedMemberIds = selectedMemberIdsByProgram[newProgram];
  const addedMemberIds = memberIdsToSelect.filter(
    memberId => !alreadySelectedMemberIds.includes(memberId),
  );

  if (addedMemberIds.length === 0) return null;

  const conflictingPrograms = CONFLICTING_PROGRAMS[newProgram].filter(program =>
    selectedMemberIdsByProgram[program].some(memberId =>
      addedMemberIds.includes(memberId),
    ),
  );

  if (conflictingPrograms.length === 0) return null;

  // Keeps the household ordering of the checkbox list the selection came from
  const conflictingMemberIds = addedMemberIds.filter(memberId =>
    conflictingPrograms.some(program =>
      selectedMemberIdsByProgram[program].includes(memberId),
    ),
  );

  const shouldRecommendMedicaid =
    newProgram === "MARKETPLACE" &&
    conflictingPrograms.length === 1 &&
    conflictingPrograms[0] === "MEDICAID" &&
    conflictingMemberIds.every(memberId =>
      medicaidEligibleMemberIds.includes(memberId),
    );

  return {
    newProgram,
    conflictingPrograms,
    memberIdsToSelect,
    conflictingMemberIds,
    shouldRecommendMedicaid,
  };
};

/**
 * Narrows the seeded selection so the form never opens in the state the
 * warning exists to prevent. Eligibility is untouched — the member is still
 * shown as eligible for both. Every conflict pair involves Marketplace today,
 * so letting it yield covers all of them; a pair without Marketplace would
 * need this widened.
 */
export const excludeConflictingSelections = (
  selectedMemberIdsByProgram: Record<ApplicationTypeEnumType, string[]>,
): Record<ApplicationTypeEnumType, string[]> => {
  const memberIdsInConflictingPrograms = new Set(
    CONFLICTING_PROGRAMS.MARKETPLACE.flatMap(
      program => selectedMemberIdsByProgram[program],
    ),
  );

  return {
    ...selectedMemberIdsByProgram,
    MARKETPLACE: selectedMemberIdsByProgram.MARKETPLACE.filter(
      memberId => !memberIdsInConflictingPrograms.has(memberId),
    ),
  };
};

export enum OtherStatusEnum {
  NEEDS_HELP_DAILY_ACTIVITIES_STATUS = "needsHelpDailyActivitiesStatus",
  VETERAN_OR_MILITARY_MEMBER_STATUS = "veteranOrMilitaryMemberStatus",
  AMERICAN_INDIAN_OR_ALASKA_NATIVE_STATUS = "americanIndianOrAlaskaNativeStatus",
  STUDENT_FULL_TIME_18_TO_20_STATUS = "studentFullTime18to20Status",
}

export const OTHER_STATUS_MAP: Record<
  OtherStatusEnum,
  { active: string; inactive: string }
> = {
  [OtherStatusEnum.NEEDS_HELP_DAILY_ACTIVITIES_STATUS]: {
    active: needsHelpDailyActivitiesStatusEnumSchema.enum.DAILY_HELP_NEEDED,
    inactive:
      needsHelpDailyActivitiesStatusEnumSchema.enum.NO_DAILY_HELP_NEEDED,
  },
  [OtherStatusEnum.VETERAN_OR_MILITARY_MEMBER_STATUS]: {
    active:
      veteranOrMilitaryMemberStatusEnumSchema.enum.VETERAN_OR_MILITARY_MEMBER,
    inactive:
      veteranOrMilitaryMemberStatusEnumSchema.enum
        .NOT_VETERAN_OR_MILITARY_MEMBER,
  },
  [OtherStatusEnum.AMERICAN_INDIAN_OR_ALASKA_NATIVE_STATUS]: {
    active:
      americanIndianOrAlaskaNativeStatusEnumSchema.enum
        .AMERICAN_INDIAN_OR_ALASKA_NATIVE,
    inactive:
      americanIndianOrAlaskaNativeStatusEnumSchema.enum
        .NOT_AMERICAN_INDIAN_OR_ALASKA_NATIVE,
  },
  [OtherStatusEnum.STUDENT_FULL_TIME_18_TO_20_STATUS]: {
    active: studentFullTime18to20StatusEnumSchema.enum.STUDENT,
    inactive: studentFullTime18to20StatusEnumSchema.enum.NOT_STUDENT,
  },
};

// Schema for a single member's coverage status in the form
export const memberCoverageStatusFormSchema = z.object({
  healthInsuranceOrCoverageStatus: z
    .array(healthInsuranceOrCoverageStatusEnumSchema)
    .optional(),
  healthInsuranceOrCoverageOptionsOther: z.string().optional(),
  medicarePlanType: medicarePlanTypeEnumSchema.nullish(),
});

// Form schema is an object with member IDs as keys
export const healthInsuranceOrCoverageStatusFormSchema = z.record(
  z.string(), // member ID as key
  memberCoverageStatusFormSchema,
);

export type HealthInsuranceOrCoverageStatusFormType = z.infer<
  typeof healthInsuranceOrCoverageStatusFormSchema
>;

export const memberCitizenshipStatusFormSchema = z.object({
  citizenshipStatus: citizenshipStatusEnumSchema.nullish(),
});

export type MemberCitizenshipStatusFormSchemaType = z.infer<
  typeof memberCitizenshipStatusFormSchema
>;

export const memberChildCaregiverStatusFormSchema = z.object({
  childCaregiverStatus: childCaregiverStatusEnumSchema.nullish(),
});

export type MemberChildCaregiverStatusFormSchemaType = z.infer<
  typeof memberChildCaregiverStatusFormSchema
>;

// Broker-consent is omitted: it is a conditional preamble and its back button is hidden.
export const APPLICATION_STEP_PATHNAMES = [
  "/(authed)/(modal)/application/citizenship-status",
  "/(authed)/(modal)/application/coverage-status",
  "/(authed)/(modal)/application/date-of-birth",
  "/(authed)/(modal)/application/address",
  "/(authed)/(modal)/application/marital-status",
  "/(authed)/(modal)/application/child-caregiver-status",
  "/(authed)/(modal)/application/other-statuses",
  "/(authed)/(modal)/application/tax-information",
  "/(authed)/(modal)/application/work-and-income",
  "/(authed)/(modal)/application/household-expenses",
  "/(authed)/(modal)/application/documents",
  "/(authed)/(modal)/application/submit",
] as const;

export const APPLICATION_FLOW_DEFAULT_STEPS = APPLICATION_STEP_PATHNAMES.length;

// Index into APPLICATION_STEP_PATHNAMES for the screen to resume on.
export const applicationResumeStepIndexByLastStep = {
  BROKER_CONSENT: 0,
  CITIZENSHIP_STATUS: 1,
  COVERAGE_STATUS: 2,
  DATE_OF_BIRTH: 3,
  ADDRESS: 4,
  MARITAL_STATUS: 5,
  CHILD_CAREGIVER_STATUS: 6,
  OTHER_STATUSES: 7,
  TAX_INFORMATION: 8,
  WORK_AND_INCOME: 9,
  HOUSEHOLD_EXPENSES: 10,
  DOCUMENTS: 11,
} as const satisfies Record<ApplicationLastStepEnumType, number>;
